import { IPasswordSecurity } from '@/entities/admin-app/filials';
import { allowedPasswordCharactersString } from '@/utils/passwordUtils';
import { useTranslation } from 'react-i18next';

export interface RuleOfPassword {
  condition: boolean;
  label: string;
  shortLabel: string;
}

const escapeForRegex = (str: string): string => {
  return str.replace(/[-\\[\]^]/g, '\\$&');
};

export const generatePasswordRules = (
  password: string,
  options?: IPasswordSecurity,
  maxLengthPassword = 30,
  t?: (key: string, params?: Record<string, any>) => string
): RuleOfPassword[] => {
  if (!options || typeof t !== 'function') return [];

  const escapedAllowedChars = escapeForRegex(allowedPasswordCharactersString);
  const allowedCharClass = `a-zA-Z0-9${escapedAllowedChars}`;

  const rules = [
    options.MinLength !== undefined && {
      condition:
        password.length >= (options.MinLength || 8) && password.length <= maxLengthPassword,
      label: t('register.passwordLong', {
        MinLength: options.MinLength || 8,
        MaxLength: maxLengthPassword
      }),
      shortLabel: t('register.passwordLongShort', {
        MinLength: options.MinLength || 8,
        MaxLength: maxLengthPassword
      })
    },

    options.UseLowLetter && {
      condition: /[a-z]/.test(password),
      label: t('register.passwordLetters'),
      shortLabel: t('register.passwordLettersShort')
    },

    options.UseDigital && {
      condition: /[0-9]/.test(password),
      label: t('register.passwordNumbers'),
      shortLabel: t('register.passwordNumbersShort')
    },

    options.UseCapitalLetters && {
      condition: /[A-Z]/.test(password),
      label: t('register.passwordUppercase'),
      shortLabel: t('register.passwordUppercaseShort')
    },

    options.UseSpecialCharacters && {
      condition: options.UseAllowedSpecialCharacters
        ? new RegExp(`[${escapedAllowedChars}]`).test(password)
        : /[^a-zA-Z0-9]/.test(password), // любой не-буквенно-цифровой
      label: t('register.passwordSpecialChars'),
      shortLabel: t('register.passwordSpecialCharsShort')
    },

    options.UseAllowedSpecialCharacters && {
      condition: (() => {
        const allowedRegex = new RegExp(`[${escapedAllowedChars}]`);
        const disallowedRegex = new RegExp(`[^${allowedCharClass}]`);
        return allowedRegex.test(password) && !disallowedRegex.test(password);
      })(),
      label: t('register.allowedSpecialChars', {
        characters: allowedPasswordCharactersString
      }),
      shortLabel: t('register.allowedSpecialCharsShort', {
        characters: allowedPasswordCharactersString
      })
    },

    options.UseLatin && {
      condition: /^[\x20-\x7E]*$/.test(password),
      label: t('register.passwordLatinOnly'),
      shortLabel: t('register.passwordLatinOnlyShort')
    }
  ];

  return rules.filter(Boolean) as RuleOfPassword[];
};

export const useGenerateRules = (
  password: string,
  options?: IPasswordSecurity,
  maxLengthPassword = 30
) => {
  const { t } = useTranslation();
  return generatePasswordRules(password, options, maxLengthPassword, t);
};
